/** @module csi/monitor/_base */
define([
    "dojo/on",
    "underscore",
    "./NodeMonitor",
    "./WebSocketNodeMonitor",
    "./PollingNodeMonitor"
], function (on, _, NodeMonitor, WebSocketNodeMonitor, PollingNodeMonitor) {

    /**
     * The array of NodeMonitor-s in the priority of possible creations
     */
    const SUPPORTED_NODE_MONITORS = [WebSocketNodeMonitor, PollingNodeMonitor];

    /**
     * Associative container of node monitors:
     * keys are URLs of monitored nodes,
     * values are instances of classes derived from NodeMonitor
     */
    const nodeMonitors = {};

    /**
     * Return url for given node or url
     * @param {string|Node} nodeOrUrl
     * @return {string}
     */
    function toUrl(nodeOrUrl) {
        return _.isObject(nodeOrUrl) ? nodeOrUrl.$URL : nodeOrUrl;
    }

    /**
     * Get NodeMonitor for the given node/url
     * @param {string|Node} nodeOrUrl - Node or URL of Node to monitor
     * @return {NodeMonitor}
     */
    function getNodeMonitor(nodeOrUrl) {
        const url = toUrl(nodeOrUrl);
        let monitor = nodeMonitors[url];

        if (!monitor) {
            _.some(SUPPORTED_NODE_MONITORS, function (nodeMonitorFactory) {
                monitor = nodeMonitorFactory.createMonitorForUrl(url);
                return monitor;
            });

            if (!monitor) {
                console.warn("Cannot create a node monitor for", url);
                return null;
            }

            // when last event listener is removed, then remode the node monitor as well
            monitor.onDisconnect = () => {
                // console.log("monitor delete: ", url);
                delete nodeMonitors[url];
            };

            // console.log("monitor create: ", url);
            nodeMonitors[url] = monitor;
        }

        return monitor;
    }

    /**
     * A function returning true or false indicating if the node condition is met
     * @callback nodeMonitorChangeCallback
     * @param {Node} node
     */

    /**
     * A function returning true or false indicating if the node condition is met
     * @callback nodeMonitorDeleteCallback
     * @param {string} url
     */

    /**
    * Monitor changes to the given node
    * @param {string|Node} nodeOrUrl
    * @param {nodeMonitorChangeCallback} callback
    */
    function onChangeNode(nodeOrUrl, callback) {
        const nodeMonitor = getNodeMonitor(nodeOrUrl);
        return on(nodeMonitor, NodeMonitor.NODE_CHANGED, m => callback(m.node));
    }

    /**
    * Monitor deletion of the given node
    * @param {string|Node} nodeOrUrl
    * @param {nodeMonitorDeleteCallback} callback
    */
    function onDeleteNode(nodeOrUrl, callback) {
        const url = toUrl(nodeOrUrl);
        const nodeMonitor = getNodeMonitor(url);
        return on(nodeMonitor, NodeMonitor.NODE_DELETED, m => callback(url));
    }

    /**
     * Get new version of the node even if no change
     * @param {string|Node} nodeOrUrl
     * @return {Deferred}
     */
    function refreshNode(nodeOrUrl) {
        const nodeMonitor = getNodeMonitor(nodeOrUrl);
        return nodeMonitor.refreshNode();
    }

    /**
     * Monitor changes to the given node
     * @param {string|Node} nodeOrUrl
     * @param {number} pollingRefreshInterval - refresh interval in milliseconds in case where polling is used; this will apply to anyone monitoring this node
     */
    function setPollingRefreshInterval(nodeOrUrl, interval) {
        const nodeMonitor = getNodeMonitor(nodeOrUrl);
        nodeMonitor.setRefreshInterval(interval);
    }

    /**
     * A function returning true or false indicating if the node condition is met
     * @callback nodeMonitorConditionCallback
     * @param {Node} node
     * @return {boolean}
     */

    /**
     * Start node monitor waiting for certain condition on the node
     * @param {string|Node} nodeOrUrl - Node or URL of Node to monitor
     * @param {nodeMonitorConditionCallback} conditionFunction - function to call to test for node condition (Node can be null if deleted)
     * @param {number} [pollingRefreshInterval] - refresh interval in milliseconds in case where polling is used (default 5000ms)
     * @param {number} [maxWaitTime] - maximum time to wait for condition to occur in milliseconds; if not specified, then will wait forever (default 0)
     * @return {Promise} - resolved once condition is met with new Node, or rejected if wait time reached
     */
    function waitForNodeCondition(nodeOrUrl, conditionFunction, pollingRefreshInterval, maxWaitTime) {
        if (pollingRefreshInterval) {
            setPollingRefreshInterval(nodeOrUrl, pollingRefreshInterval);
        }

        return new Promise(function (resolve, reject) {
            let waitHandle;
            let eventHandle;

            if (maxWaitTime) {
                waitHandle = setTimeout(() => {
                    eventHandle.remove();
                    reject();
                }, maxWaitTime);
            }

            eventHandle = onChangeNode(nodeOrUrl, node => {
                if (conditionFunction(node)) {
                    clearTimeout(waitHandle);
                    eventHandle.remove();
                    resolve(node);
                }
            });
        });
    }

    return {
        onChangeNode,
        onDeleteNode,
        refreshNode,
        setPollingRefreshInterval,
        waitForNodeCondition,
    };
});